import { ChangeDetectorRef, Component, ViewChild, OnInit } from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DashboardService} from '../../../../../service/events/dashboard.service';
import {BaseForm} from '../../../../base-form';
import {VentasApiService} from '../../../../../service/api/ventas-api.service';
import {FincasApiService} from '../../../../../service/api/fincas-api.service';
import {FormCommonApiService} from '../../../../../service/api/formcommon-api.service';
import { build, pool } from '@fitogest/reactive-form-builder/utils';
import {StorageManager} from '../../../../../common/storage-manager.class';
import { environment } from '../../../../../../environments/environment';
import { RequestButtonComponent } from '../../../../../common/components/request-button/request-button.component';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { Filtering } from '../../../../../service/filtering/filtering';
import { SiexApiService } from '../../../../../service/api/siex-api.service';

import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { VentaModel } from 'src/app/models/ventas/venta.model';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { FormPhase, ReactiveFormRequestCoordinator } from '@fitogest/reactive-form-builder/form-request';
import { VentasFormDefinition } from './form-definition';
import {
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';

const PREVIOUS_DATA_PATCH_FIELD = '__previous_data_patch';
const DROPDOWN_REFRESH_FIELD = '__dropdown_refresh';
const DEFERRED_DROPDOWN_LOAD_DELAY_MS = 0;

@Component({
    selector: 'app-nueva-carga',
    standalone: false,
    templateUrl: './ventas-form.component.html',
    styleUrls: ['./ventas-form.component.scss']
})
export class VentasFormComponent extends BaseForm<VentaModel> implements OnInit {
    public readonly reactiveFormLoadingLabel = 'Preparando formulario de ventas...';

    @ViewChild(RequestButtonComponent, { read: RequestButtonComponent, static: true })
            requestButton = new RequestButtonComponent<VentaModel>();

    public form!: VentasFormDefinition;

    public formRequest = new ReactiveFormRequestCoordinator<VentaModel>;

    public loadingData = true;
    public isReactiveFormReady = false;
    public reactiveFormLoadingError = '';
    public getRequest = this.ventasApi.ventasGET;
    public initialPreviousData: VentaModel | null = null;
    public reactiveFormRecordId = '';

    public override model: VentaModel = {};

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public clientesApi: ClientesApiService,
        public ventasApi: VentasApiService,
        public fincasApi: FincasApiService,
        public formApi: FormCommonApiService,
        public siexApi: SiexApiService,
        public override router: Router,
        private readonly cdr: ChangeDetectorRef
    ) {
        super(
            route,
            router,
            dashboard,
            ventasApi.ventasPUT,
            ventasApi.ventasPOST,
            'ventas',
            'Actualizar ' + environment.features.ventaName,
            'Crear ' + environment.features.ventaName
        );
    }

    public async ngOnInit(): Promise<void> {
        await this.initializeVentasForm();
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<VentaModel>): void {
        const changedFieldName = String(event.changedField ?? '');

        this.captureStableValueChange(event.value as VentaModel);
        this.form.model = this.toVentaModel({
            ...this.form.model,
            ...event.value,
        });
        this.form.tag = event.changedField;
        this.syncModelFromForm();

        if (changedFieldName === PREVIOUS_DATA_PATCH_FIELD || changedFieldName === DROPDOWN_REFRESH_FIELD) {
            this.refreshReactiveDropdownMappings();
            return;
        }

        if (event.origin !== 'user') {
            return;
        }

        this.handleUserValueChange(event.value, event.changedField);
    }


    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<VentaModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            if (event.type === 'dropdownDataResolved' || event.type === 'dropdownRefresh') {
                this.refreshReactiveDropdownMappings();
            }

            return;
        }

        this.form.model = this.toVentaModel({
            ...this.form.model,
            ...event.value,
        });
        this.syncModelFromForm();
        this.refreshReactiveDropdownMappings();
    }


    private async initializeVentasForm(): Promise<void> {
        try {
            this.loadingData = true;
            this.isReactiveFormReady = false;
            this.reactiveFormLoadingError = '';
            this.resetExternalDropdowns();
            this.softInit(this.getType());
            const routeModel = this.toVentaModel(this.getModel() as unknown as Record<string, unknown>);

            this.reactiveFormRecordId = this.resolveReactiveFormRecordId(routeModel);
            this.model = this.initializeHostModel(routeModel);
            this.initialPreviousData = this.getInitialPreviousData();
            this.form = new VentasFormDefinition(
                this.getType(),
                this.model as Record<string, string>,
                null
            );
            this.form.model = this.toVentaModel(this.model);
            this.syncModelFromForm();

            await this.initFormData();
            await this.formRequest.executePhase(FormPhase.LOAD);
            this.loadingData = false;
            this.markReactiveFormReady();
            this.scheduleDeferredDropdownCatalogs();
        } catch (error: unknown) {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            this.cdr.markForCheck();
            console.error('No se ha podido preparar el formulario de ventas.', error);
        }
    }


    private async initFormData(): Promise<void> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        try {
            await this.loadDropdownCatalogPhase('critical');
            this.applyFincaCreationDefaults();
            this.postProcessDropdowns();
            this.finalizeExternalDropdowns();
            this.refreshReactiveDropdownMappings();
        } catch (error: unknown) {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.refreshReactiveDropdownMappings();
            throw error;
        }
    }


    private async loadDropdownCatalogPhase(phase: 'critical' | 'deferred'): Promise<void> {
        const catalogFields = this.form.formFields.dropdownCatalogs?.[phase] ?? [];
        const builds = catalogFields
            .map((fieldName) => this.createDropdownCatalogBuild(String(fieldName)))
            .filter((catalogBuild): catalogBuild is (resolve: (ok: boolean) => void) => void => catalogBuild !== null);

        if (builds.length === 0) {
            return;
        }

        await pool(builds);
    }


    private createDropdownCatalogBuild(fieldName: string): ((resolve: (ok: boolean) => void) => void) | null {
        if (fieldName === 'id_finca') {
            return build(this.fincasApi.fincasGET, this.form.lists.fincas, 'nombre', true);
        }

        if (fieldName === 'id_sector') {
            return build(this.fincasApi.sectoresGET, this.form.lists.sectores, 'nombre', true);
        }

        if (fieldName === 'ids_parcelas') {
            return build(
                this.fincasApi.parcelasGET,
                this.form.lists.parcelas,
                this.form.env.appName === 'avafitogest' ? this.parcelaLabel : 'nombre',
                true,
                true
            );
        }

        if (fieldName === 'id_variedad') {
            return build(
                this.form.env.activateSIEX && !this.form.env.hasPlantas
                    ? this.siexApi.variedadesClienteGET
                    : this.formApi.variedadesGET,
                this.form.lists.variedades,
                'variedad',
                true
            );
        }

        if (fieldName === 'id_comprador') {
            return build(this.ventasApi.compradoresGET, this.form.lists.compradores, 'razon_social', true);
        }

        if (fieldName === 'id_cliente') {
            return build(this.clientesApi.clientesGET, this.form.lists.clientes, 'nombre');
        }

        if (fieldName === 'clientes_comercios') {
            return this.form.env.appName === 'pyf'
                ? build(this.ventasApi.compradoresGET, this.form.lists.clientes_comercios, 'razon_social', true)
                : null;
        }

        if (fieldName === 'cooperativas') {
            return this.form.env.showClientesCooperativa
                ? build(this.clientesApi.cooperativasGET, this.form.lists.cooperativas, 'cooperativa')
                : null;
        }

        return null;
    }

    private scheduleDeferredDropdownCatalogs(): void {
        globalThis.setTimeout((): void => {
            this.loadDropdownCatalogPhase('deferred')
                .then((): void => {
                    this.postProcessDropdowns();
                    this.finalizeExternalDropdowns();
                    this.refreshReactiveDropdownMappings();
                    this.cdr.markForCheck();
                })
                .catch((error: unknown): void => {
                    console.warn('[ventas dropdowns] deferred dropdowns failed.', error);
                });
        }, DEFERRED_DROPDOWN_LOAD_DELAY_MS);
    }


    private markReactiveFormReady(): void {
        this.isReactiveFormReady = true;
        this.cdr.detectChanges();
        this.finalizeExternalDropdowns();
        this.refreshReactiveDropdownMappings();
        this.cdr.markForCheck();
    }


    private handleUserValueChange(
        formModel: ReactiveFormValue<VentaModel>,
        lastChangedFieldName: keyof ReactiveFormValue<VentaModel> | string | null,
    ): void {
        const changedFieldName = String(lastChangedFieldName ?? '');

        this.form.model = this.toVentaModel({
            ...this.form.model,
            ...formModel,
        });
        this.syncModelFromForm();

        if (changedFieldName === 'id_finca' || changedFieldName === 'id_sector' || changedFieldName === 'ids_parcelas') {
            this.refreshReactiveDropdownMappings();
        }
    }


    private applyFincaCreationDefaults(): void {
        if (this.form.env.appName === 'agroleza') {
            return;
        }

        const fincas = this.form.lists.fincas.values
            .map((option) => option.value)
            .filter((value): value is FincasModel => value !== null && typeof value === 'object');

        if (this.form.env.appName === 'campanaragricola') {
            const userFinca = fincas.find((finca) => finca.id_usuario === StorageManager.getUser().id);

            if (userFinca?.id) {
                this.form.model.id_finca = userFinca.id;
                this.syncModelFromForm();

                return;
            }
        }

        if (fincas.length === 1) {
            this.form.model.id_finca = fincas[0]?.id ?? '';
            this.syncModelFromForm();
        }
    }


    private postProcessDropdowns(): void {
        this.form.lists.variedades.values = (this.form.lists.variedades.values || []).filter(
            (item, index, self) =>
                index === self.findIndex(
                    other =>
                        (other.value?.id === item.value?.id) ||
                        (other.value?.variedad === item.value?.variedad)
                )
        );
        this.form.lists.variedades.filtered = this.form.lists.variedades.values;

        if (this.form.env.appName === 'campanaragricola') {
            const userFincaId = StorageManager.getUser().id_finca?.toString() ?? '';

            this.form.lists.fincas.filtered = (this.form.lists.fincas.filtered || [])
                .filter((item) => item.value !== null)
                .filter((item) => [item.value?.id?.toString(), item.value?.nombre?.toString()].includes(userFincaId))
                .map((item) => ({
                    label: item.value?.nombre ?? '',
                    value: item.value,
                }))
                .sort(Filtering.sort('label', 1));

            this.form.lists.fincas.filtered.unshift({
                label: '...',
                value: null,
            });
        }

        if (this.form.env.appName === 'avafitogest') {
            this.form.lists.parcelas.values = (this.form.lists.parcelas.values || []).map(it => ({
                ...it,
                label: this.parcelaLabel(it.value as ParcelasModel)
            }));
            this.form.lists.parcelas.filtered = this.form.lists.parcelas.values;
        }
    }

    private getInitialPreviousData(): VentaModel | null {
        if (this.getType() !== this.formTypes.DUPLICATION) {
            return null;
        }

        return this.toVentaModel(this.model);
    }


    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return 'No se ha podido cargar el formulario de ventas: ' + error.message;
        }

        return 'No se ha podido cargar el formulario de ventas. Revisa la conexión e inténtalo de nuevo.';
    }


    private refreshReactiveDropdownMappings(): void {
        this.formBuilderRef?.refreshExternalDropdownMappings();
    }


    private syncModelFromForm(): void {
        const normalizedModel = this.toVentaModel(this.form.model);

        this.form.model = normalizedModel;
        this.updateHostModel(normalizedModel);
    }


    private toVentaModel(
        sourceModel: ReactiveFormValue<VentaModel> | Partial<VentaModel> | Record<string, unknown>,
    ): VentaModel {
        const normalizedModel: Record<string, VentaModel[keyof VentaModel]> = {};

        for (const [key, value] of Object.entries(sourceModel)) {
            if (value !== undefined) {
                normalizedModel[key] = value as VentaModel[keyof VentaModel];
            }
        }

        return normalizedModel as VentaModel;
    }


    private parcelaLabel = (p: ParcelasModel): string => {
        const nombre = (p.nombre ?? '').toString().trim();
        const variedad = (p.variedad ?? 'SIN VARIEDAD').toString().trim();

        let zonaRiegoAbrev = '';
        if ((p.cultivo ?? '').toLowerCase() === 'arroz') {
            const zr = (p.zona_riego ?? '').toString().toLowerCase();
            if (zr.includes('alta')) zonaRiegoAbrev = 'ZA';
            else if (zr.includes('baja')) zonaRiegoAbrev = 'ZB';
        }

        const variedadConZona = zonaRiegoAbrev
            ? `${variedad} - ${zonaRiegoAbrev}`
            : variedad;

        const pol = (p.poligono ?? '').toString().trim();
        const num = (p.numero ?? '').toString().trim();
        const rec = (p.recinto ?? '').toString().trim();
        const codigos = [pol, num, rec].filter(Boolean).join('-');

        const supRaw = (p.superficie_cultivada ?? p.superficie_sigpac ?? p.superficie ?? '0').toString();
        const supNum = parseFloat(supRaw.replace(',', '.')) || 0;
        const supFmt = supNum.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

        const uds = environment.features.textoSuperficie || 'ha';

        const derecha = [codigos || undefined, `${supFmt} ${uds}`]
            .filter(Boolean)
            .join(' · ');

        return `${nombre} - ${variedadConZona} — ${derecha}`;
    };
}
